import * as Highcharts from 'highcharts';

// Highcharts v12 modules self-register on the shared singleton at import time —
// there are no more factory functions to call. Import ORDER is therefore load
// order and must be preserved: `exporting` has to evaluate before `export-data`
// and `offline-exporting`, which compose onto it (otherwise composing reads
// `Exporting.prototype` off `undefined`). Bare side-effect imports are used (not
// default imports) so the import sorter keeps them in this dependency order
// instead of re-sorting alphabetically. The export modules power Chart/Matrix
// CSV + PNG download and run fully client-side (no data sent to Highcharts'
// export servers); heatmap powers Matrix; highcharts-more provides the bubble
// series type (scatter is core).
//
// Explicit `.js` — highcharts ships no `exports` map, so strict Node ESM
// (unlike bundlers) refuses to resolve these extensionless subpaths.
import 'highcharts/highcharts-more.js';
import 'highcharts/modules/exporting.js';
import 'highcharts/modules/export-data.js';
import 'highcharts/modules/offline-exporting.js';
import 'highcharts/modules/heatmap.js';

// Highcharts v12 attaches the export-data methods to the `Exporting` class at
// runtime (`chart.exporting.getCSV()` / `downloadCSV()`), but its bundled type
// declarations omit them. Re-declare the ones we use so callers stay type-safe
// without casts.
declare module 'highcharts' {
	interface Exporting {
		getCSV(useLocalDecimalPoint?: boolean): string;
		downloadCSV(): void;
	}
}

// useToken returns CSS var refs for semantic tokens, and Chakra semantic tokens
// often chain (primary.bg → var(--…-blue-500) → hex). CSS vars don't resolve
// during offline PNG rasterization and can't be interpolated (the heatmap color
// gradient), so resolve the whole chain to its leaf hex up front. Depth-capped.
export function resolveCssColor(color: string, depth = 0): string {
	const match = /^var\((--[^,)\s]+)/.exec(color.trim());
	if (!match || typeof document === 'undefined' || depth > 5) return color;
	const resolved = getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim();
	if (!resolved) return color;
	return resolveCssColor(resolved, depth + 1);
}

export { Highcharts };
